import { VaporToolbar } from "@vapor/react-custom";
import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Box,
    Button,
    CircularProgress,
    FormHelperText,
    Stack,
    TextareaAutosize,
    useTheme,
} from "@vapor/react-material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FulfilmentActivityDto } from "../../../../../core/api/use-get-fulfilment-activities/FulfilmentActivityDto";
import { useUpdateActivity } from "../../../../../core/api/use-update-activity/useUpdateActivity";
import { useSuccessNotification } from "../../../../../core/hooks/useSuccessNotification";
import { MAX_NOTE_LENGTH, MAX_TITLE_LENGTH } from "../add-activity/AddActivity";

interface FulfimentActivityEditProps {
    activity: FulfilmentActivityDto;
    levyId: string;
    onClickCancel: () => unknown;
    onUpdateTitle: () => unknown;
    taxPayerWorkspaceId: string;
    year: number;
}

export const FulfimentActivityEdit: React.FC<FulfimentActivityEditProps> = ({
    activity,
    levyId,
    onClickCancel,
    onUpdateTitle,
    taxPayerWorkspaceId,
    year,
}): JSX.Element => {
    const { t } = useTranslation();
    const { showTextMessage } = useSuccessNotification();
    const theme = useTheme();

    const [data, setData] = useState<{ notes?: string; title?: string }>({
        notes: activity.notes,
        title: activity.title,
    });

    const updateActivityMutation = useUpdateActivity();

    const isTitleValid = useMemo(() => {
        return data && data.title && data.title.length <= MAX_TITLE_LENGTH;
    }, [data]);

    const isNoteValid = useMemo(() => {
        return !data.notes || data.notes.length <= MAX_NOTE_LENGTH;
    }, [data]);

    const isDataValid = useMemo(() => {
        return (
            isTitleValid &&
            isNoteValid &&
            ((data.notes?.trim() || undefined) !==
                (activity.notes?.trim() || undefined) ||
                data.title !== activity.title)
        );
    }, [activity.notes, activity.title, data, isNoteValid, isTitleValid]);

    const handleUpdateActivity = async () => {
        try {
            await updateActivityMutation.mutateAsync({
                activityId: activity.id,
                notes: data.notes,
                title: data.title,
                taxPayerWorkspaceId: taxPayerWorkspaceId,
                levyId: levyId,
                year: year,
            });
        } catch {
            return;
        }
        showTextMessage(
            t(
                "views.fulfilment_detail.components.edit_activity.edit_activity.successfull",
            ),
        );
        onUpdateTitle();
    };

    return (
        <Stack p={3}>
            <Stack gap={1} justifyContent="space-between">
                <Box>
                    <Typography variant={"body500"}>
                        {t(
                            "views.fulfilment_detail.components.edit_activity.edit_title.title.label",
                        )}
                    </Typography>
                    <Stack gap={1}>
                        <TextareaAutosize
                            disabled={updateActivityMutation.isPending}
                            error={!isTitleValid}
                            onChange={(event) => {
                                setData((prev) => ({
                                    ...prev,
                                    title: event.target.value,
                                }));
                            }}
                            minRows={1}
                            placeholder={t(
                                "views.fulfilment_detail.components.edit_activity.edit_title.title.placeholder",
                            )}
                            style={{
                                borderColor: !isTitleValid
                                    ? theme.palette.primary.accentRustyRed
                                    : undefined,
                            }}
                            value={data.title}
                        />
                        {data &&
                            data.title &&
                            data.title.length > MAX_TITLE_LENGTH && (
                                <FormHelperText>
                                    {t(
                                        "views.fulfilment_detail.components.edit_activity.edit_title.title.text_helper",
                                        { number: MAX_TITLE_LENGTH },
                                    )}
                                </FormHelperText>
                            )}
                    </Stack>
                </Box>
                <Box>
                    <Typography variant={"body500"}>
                        {t(
                            "views.fulfilment_detail.components.edit_activity.edit_activity.notes.label",
                        )}
                    </Typography>
                    <Stack gap={1}>
                        <TextareaAutosize
                            disabled={updateActivityMutation.isPending}
                            error={!isNoteValid}
                            onChange={(event) => {
                                setData((prev) => ({
                                    ...prev,
                                    notes: event.target.value,
                                }));
                            }}
                            minRows={1}
                            placeholder={t(
                                "views.fulfilment_detail.components.edit_activity.edit_activity.notes.placeholder",
                            )}
                            style={{
                                borderColor: !isNoteValid
                                    ? theme.palette.primary.accentRustyRed
                                    : undefined,
                            }}
                            value={data.notes}
                        />
                        {!isNoteValid && (
                            <FormHelperText>
                                {t(
                                    "views.fulfilment_detail.components.edit_activity.edit_activity.notes.text_helper",
                                    { number: MAX_NOTE_LENGTH },
                                )}
                            </FormHelperText>
                        )}
                    </Stack>
                </Box>
            </Stack>
            <Box
                sx={{
                    ml: -3,
                }}
            >
                <VaporToolbar
                    contentRight={[
                        <Button
                            disabled={
                                !isDataValid || updateActivityMutation.isPending
                            }
                            onClick={handleUpdateActivity}
                            size="large"
                            variant="contained"
                        >
                            {t(
                                "views.fulfilment_detail.components.edit_activity.edit_activity.confirm",
                            )}
                            {updateActivityMutation.isPending && (
                                <CircularProgress
                                    color="inherit"
                                    sx={{ ml: 1 }}
                                    size={16}
                                />
                            )}
                        </Button>,
                    ]}
                    contentLeft={[
                        <Button
                            disabled={updateActivityMutation.isPending}
                            onClick={onClickCancel}
                            size="large"
                            variant="outlined"
                        >
                            {t(
                                "views.fulfilment_detail.components.edit_activity.edit_activity.cancel",
                            )}
                        </Button>,
                    ]}
                    size="large"
                    variant="regular"
                />
            </Box>
        </Stack>
    );
};
